// Lo que queda GUARDADO en un mensaje del usuario sobre lo que adjuntó —
// ADR-2026-09-08-agregar-productos-es-una-herramienta.
//
// Hasta acá el motor consumía los chips al enviar y la burbuja del usuario mostraba sólo
// el texto: "le cargué fotos y en el chat no aparecen". Ahora el mensaje lleva un resumen
// de cada adjunto, con lo justo para volver a DIBUJARLO y para que una herramienta
// posterior lo ENCUENTRE (`agregar_productos` toma las fotos del último mensaje que las
// tenga). Sin URLs firmadas: caducan y el chat se persiste en `agent_chats`. La imagen
// guarda su path del storage y se vuelve a firmar al dibujar; el producto guarda su id y
// el catálogo pone la miniatura.
//
// Puro, para poder probarlo.

import { leerFirma } from '../../utils/firmaDeStorage';
import type { ChatAttachment } from './agentAttachments';
import { fotosDelMoodboard } from './etiquetaDeAdjunto';

export type AdjuntoDeMensaje =
  /** `imageId`: si la imagen es una CREACIÓN del usuario (viene de sus generadas), el
   *  id con el que se la puede volver a usar como referencia exacta (source "generada")
   *  — ADR-2026-09-13-los-adjuntos-dicen-su-id. */
  | { kind: 'image'; storagePath: string; bucket?: string; imageId?: string }
  | { kind: 'texto'; name: string; bytes: number }
  | { kind: 'product'; productId: string; name: string }
  /** `modelId`: la modelo guardada elegida, por id. Con dos de nombre parecido
   *  ("Ruperta" y "Ruperta pro") el nombre solo no alcanza. */
  | { kind: 'model'; name: string; imageUrl: string | null; modelId?: string }
  /**
   * Una referencia del catálogo de la marca (pose, fondo, foto de moodboard).
   *
   * Guarda el PATH de Storage y no la URL firmada, que vence en una hora y deja el chip
   * roto y la referencia irrecuperable — es la misma regla que ya seguían la imagen y el
   * video, y ésta era la única que la incumplía
   * (ADR-2026-09-14-la-referencia-guardada-no-puede-ser-una-firma). `imageUrl` queda para
   * las URLs que NO son de nuestro Storage (públicas, que no caducan) y para lo guardado
   * antes de este cambio.
   */
  | {
      kind: 'referencia';
      tipo: 'pose' | 'fondo' | 'moodboard' | 'elemento' | 'tipografia' | 'pinterest';
      name: string;
      storagePath?: string;
      bucket?: string;
      imageUrl?: string;
      /**
       * El id de la pose o el fondo en su catálogo. Con esto el agente la puede REUSAR en
       * un mensaje posterior (`reference_images` con source "pose"/"fondo") sin pedirle al
       * usuario que la vuelva a adjuntar
       * (ADR-2026-09-14-la-pose-y-el-fondo-son-referencias-por-id). Ausente en las fotos de
       * moodboard, que no son una entrada de catálogo.
       */
      refId?: string;
    }
  /** Un moodboard (puntero): la carpeta, cuántas fotos tenía y, si se eligió una parte,
   *  cuáles (`fotoIds` — ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes). De acá
   *  saca `ver_moodboard` qué fotos mostrar. */
  | { kind: 'moodboard'; moodboardId: string; name: string; cantidadFotos: number; fotoIds?: string[] }
  /** Una planilla (Excel/CSV). El contenido vive en memoria; acá sólo su forma. */
  | { kind: 'planilla'; name: string; hojas: number; filas: number; planillaId: string }
  /** Un PDF. Los bytes viven en memoria; acá sólo su forma. */
  | { kind: 'pdf'; name: string; paginas: number; tieneTexto: boolean; pdfId: string }
  /** Un lote de fotos de productos (vive en memoria; acá sólo cuántas eran). */
  | { kind: 'lote'; loteId: string; cantidad: number }
  /** Un video de referencia. Vive en el storage: el path alcanza para que `ver_video` lo
   *  encuentre, incluso tras una recarga (ADR-2026-09-11-el-agente-entiende-videos-con-gemini). */
  | { kind: 'video'; name: string; storagePath: string; bucket?: string; duracionSeg: number; bytes: number };

/** El chip del motor → lo que se guarda en el mensaje. */
export function resumirAdjunto(a: ChatAttachment): AdjuntoDeMensaje {
  switch (a.kind) {
    case 'image': {
      const imageId = imageIdDeCreacion(a);
      return {
        kind: 'image',
        storagePath: a.storagePath,
        ...(a.bucket ? { bucket: a.bucket } : {}),
        ...(imageId ? { imageId } : {}),
      };
    }
    case 'texto':
      return { kind: 'texto', name: a.name, bytes: a.bytes };
    case 'product':
      return { kind: 'product', productId: a.productId, name: a.name };
    case 'model': {
      const modelId = modelIdDeAdjunto(a);
      return { kind: 'model', name: a.name, imageUrl: a.imageUrl, ...(modelId ? { modelId } : {}) };
    }
    case 'referencia': {
      // Si es una firma de nuestro Storage, se guarda el objeto y se vuelve a firmar al
      // dibujar. Si no lo es (URL pública), se guarda tal cual: no caduca.
      const firma = leerFirma(a.imageUrl);
      const refId = refIdDeAdjunto(a);
      return {
        kind: 'referencia',
        tipo: a.tipo,
        name: a.name,
        ...(refId ? { refId } : {}),
        ...(firma
          ? { storagePath: firma.path, bucket: firma.bucket }
          : { imageUrl: a.imageUrl }),
      };
    }
    case 'moodboard':
      return {
        kind: 'moodboard',
        moodboardId: a.moodboardId,
        name: a.name,
        cantidadFotos: a.cantidadFotos,
        ...(a.fotoIds ? { fotoIds: a.fotoIds } : {}),
      };
    case 'pdf':
      return {
        kind: 'pdf',
        name: a.name,
        paginas: a.paginas,
        tieneTexto: a.tieneTexto,
        pdfId: a.pdfId,
      };
    case 'planilla':
      return {
        kind: 'planilla',
        name: a.name,
        hojas: a.hojas,
        filas: a.filas,
        planillaId: a.planillaId,
      };
    case 'video':
      return {
        kind: 'video',
        name: a.name,
        storagePath: a.storagePath,
        ...(a.bucket ? { bucket: a.bucket } : {}),
        duracionSeg: a.duracionSeg,
        bytes: a.bytes,
      };
  }
}

/** El `image_id` de una imagen adjunta que es una CREACIÓN (la biblioteca y el selector
 *  la arman con id `gen-<image_id>` y bucket `generadas`); null para una subida. */
export function imageIdDeCreacion(a: Extract<ChatAttachment, { kind: 'image' }>): string | null {
  if (a.bucket !== 'generadas' || !a.id.startsWith('gen-')) return null;
  const id = a.id.slice(4);
  return id || null;
}

/** El id de la modelo guardada de un adjunto de modelo; null si no es una guardada. */
/**
 * El id de catálogo de una pose o un fondo enganchado. El chip lo lleva en su propio id
 * (`pose-<id>` / `fondo-<id>`, que los arma `bibliotecaDeAdjuntos`), que es también lo que
 * evita que dos poses distintas compartan chip. Las fotos de moodboard no tienen entrada
 * de catálogo y devuelven null.
 */
export function refIdDeAdjunto(a: Extract<ChatAttachment, { kind: 'referencia' }>): string | null {
  const prefijo = a.tipo === 'pose' ? 'pose-' : a.tipo === 'fondo' ? 'fondo-' : null;
  if (!prefijo || !a.id.startsWith(prefijo)) return null;
  const id = a.id.slice(prefijo.length);
  return id || null;
}

/**
 * Lo que se le dice al modelo sobre las POSES y los FONDOS que el usuario enganchó en
 * ESTE mensaje (ADR-2026-09-14-la-pose-adjunta-viaja-con-su-id-y-no-se-describe).
 *
 * Vive acá, puro y con tests, y no como un string adentro de `sendMessage`, porque ahí
 * escondido decía "describila en el prompt" y sobrevivió a las dos rondas en las que se
 * sacó esa misma instrucción de la skill: el agente le hacía caso a la copia que nadie
 * veía. Tres cosas que el texto tiene que decir bien:
 *
 * - la POSE se NOMBRA y no se describe (describirla compite con la foto);
 * - el FONDO sí se describe (el motor recrea el lugar, no lo pega);
 * - y cómo viaja cada una, que NO es lo mismo en los dos caminos: en `generate_image` las
 *   adjuntas van solas, en `generar_lote_imagenes` cada ítem tiene que pedirlas por id.
 *   Decir "el sistema ya la manda" —lo que decía antes— es falso en el lote, que es
 *   justamente donde el usuario pide varias poses.
 */
export function lineasDeReferenciasAdjuntas(
  referencias: ReadonlyArray<Extract<ChatAttachment, { kind: 'referencia' }>>,
): string[] {
  const conId = (r: Extract<ChatAttachment, { kind: 'referencia' }>, clave: string) => {
    const id = refIdDeAdjunto(r);
    return `"${r.name}"${id ? ` (${clave}=${id})` : ''}`;
  };
  const lineas: string[] = [];
  const poses = referencias.filter((r) => r.tipo === 'pose');
  const fondos = referencias.filter((r) => r.tipo === 'fondo');
  if (poses.length > 0) {
    lineas.push(
      `POSE(S) que el usuario eligió: ${poses.map((p) => conId(p, 'pose_id')).join(', ')}. ` +
        'Las VES en este mensaje, etiquetadas [POSE: ...]. Son la POSTURA de la persona. ' +
        'En el prompt NOMBRALA ("replicá la postura, el encuadre y el ángulo de `Pose de referencia:`") ' +
        'y NO la describas: describirla compite con la foto. No copies de esa imagen el fondo, la ropa ni el calzado.\n' +
        'Cómo la mandás: en generate_image las adjuntas viajan solas. En generar_lote_imagenes NO: ' +
        'cada ítem que la lleve necesita `{source:"pose", pose_id:"…"}` — y ahí es donde una serie ' +
        'lleva UNA POSE DISTINTA POR FOTO, un pose_id por ítem. Sin el pose_id la referencia se descarta.',
    );
  }
  if (fondos.length > 0) {
    lineas.push(
      `FONDO(S) que el usuario eligió: ${fondos.map((f) => conId(f, 'fondo_id')).join(', ')}. ` +
        'Los VES en este mensaje, etiquetados [FONDO: ...]. Son el ESCENARIO: describilo en el prompt ' +
        '(lugar, luz, colores) además de pasarlo como referencia — a diferencia de la pose, acá describir ' +
        'AYUDA: el motor recrea el lugar. No copies personas ni productos de esa foto.\n' +
        'Cómo lo mandás: en generate_image viaja solo; en generar_lote_imagenes, ' +
        '`{source:"fondo", fondo_id:"…"}` en cada ítem que lo lleve.',
    );
  }
  // ADR-2026-10-23-pinterest-guardados-como-referencia
  const dePinterest = referencias.filter((r) => r.tipo === 'pinterest');
  if (dePinterest.length > 0) {
    lineas.push(
      `IMAGEN(ES) DE SU PINTEREST que el usuario eligió: ${dePinterest.map((p) => `"${p.name}"`).join(', ')}. ` +
        'Las VES en este mensaje, etiquetadas [PINTEREST: ...]. Son INSPIRACIÓN: hacé algo PARECIDO con lo suyo ' +
        '(su producto, su marca), tomando la estética —composición, encuadre, luz, paleta, actitud—. No copies ' +
        'personas, marcas, logos ni textos de esa foto. Viajan solas como referencia en este turno.',
    );
  }
  // ADR-2026-10-19-elementos-de-marca
  const elementos = referencias.filter((r) => r.tipo === 'elemento');
  if (elementos.length > 0) {
    lineas.push(
      `ELEMENTO(S) DE MARCA que el usuario eligió: ${elementos.map((e) => `"${e.name}"`).join(', ')}. ` +
        'Los VES en este mensaje, etiquetados [ELEMENTO DE MARCA: ...]. Son piezas de SU marca (un logo, un elemento ' +
        'gráfico): van TAL CUAL, sin redibujarlos, recolorearlos ni inventar otros. Si no dijo dónde ni cómo usarlos, ' +
        'preguntale antes de generar. Cómo los mandás: en generate_image viajan solos como referencia; en ' +
        'generar_lote_imagenes, con source "adjunto" en cada ítem que los lleve.',
    );
  }
  // ADR-2026-10-19-la-tipografia-viaja-como-hoja-de-muestra
  const tipografias = referencias.filter((r) => r.tipo === 'tipografia');
  if (tipografias.length > 0) {
    lineas.push(
      `TIPOGRAFÍA(S) DE LA MARCA que el usuario eligió: ${tipografias.map((t) => `"${t.name}"`).join(', ')}. ` +
        'Las VES como HOJA DE MUESTRA (abecedario, números y signos), etiquetadas [TIPOGRAFÍA: ...]. Sirven para ESCRIBIR ' +
        'los textos de la pieza con esas letras: en el prompt pedí que el texto use la tipografía de la referencia ' +
        '`Tipografía de la marca «nombre»:`. NUNCA pongas la hoja ni el abecedario en la imagen. ' +
        // ADR-2026-10-20-la-tipografia-adjunta-no-se-retoca
        'La tipografía va TAL CUAL está en la hoja: no la cambies ni la "mejores". El prompt pide las mismas letras, el ' +
        'mismo peso y grosor, el mismo espaciado entre letras y el mismo color, plano, sin sombras, brillos, luces, ' +
        'contornos, degradés, relieve ni ningún efecto. Si el usuario pide otra cosa, directa o indirectamente ("en ' +
        'dorado", "estilo neón", "que resalte sobre la foto", "en blanco"), se hace lo que pidió sólo en eso y el resto ' +
        'queda igual. Sin texto en la pieza no aportan: si no hay texto, preguntale qué va escrito. Viajan solas en ' +
        'generate_image; en generar_lote_imagenes, con source "adjunto" y esa etiqueta.',
    );
  }
  return lineas;
}

export function modelIdDeAdjunto(a: Extract<ChatAttachment, { kind: 'model' }>): string | null {
  const sel = a.selection;
  return sel.mode === 'preset' && sel.id != null && String(sel.id) ? String(sel.id) : null;
}

export const resumirAdjuntos = (lista: ChatAttachment[]): AdjuntoDeMensaje[] =>
  lista.map(resumirAdjunto);

const plural = (n: number, uno: string, varios: string) => (n === 1 ? uno : varios);

/**
 * El MARCADOR que acompaña a un mensaje viejo del usuario en el historial que va a la
 * API — ADR-2026-09-13-los-adjuntos-viejos-se-recuerdan.
 *
 * El bloque `[CONTEXTO ADJUNTO POR EL USUARIO]` con el `product_id` y compañía se arma
 * sólo para el turno en curso y no se guarda: el texto del mensaje queda limpio para la
 * burbuja. En los turnos siguientes el historial llevaba ese texto pelado, y el modelo
 * veía "generá con ese" sin ningún rastro de cuál era — y le pedía al usuario que lo
 * volviera a elegir. Lo guardado en el mensaje (`adjuntos`) alcanza para reconstruir un
 * marcador corto, y es honesto por tipo: lo de catálogo se identifica por id o nombre y
 * lo que se lee con una herramienta sigue a mano; las imágenes y referencias sueltas ya
 * no viajan (su contenido fue sólo en ese turno), así que se dice que las pida de nuevo.
 * Corto a propósito: se repite en cada mensaje con adjuntos, en todos los turnos.
 */
export function marcadorDeAdjuntos(adjuntos: AdjuntoDeMensaje[] | undefined, iMensaje?: number): string {
  if (!adjuntos || adjuntos.length === 0) return '';
  const partes: string[] = [];
  let aMano = false;
  let sueltos = false;
  let subidasConId = false;
  let referenciasConId = false;
  // Las referencias que siguen en Storage (estilo, elemento de marca, tipografía), con su id
  // para reusarlas (ADR-2026-10-20-las-referencias-se-reusan-entre-turnos).
  const idsDeRefs = iMensaje === undefined ? new Map<AdjuntoDeMensaje, string>() : idsDeReferencias(adjuntos, iMensaje);
  const creaciones = adjuntos.filter((a) => a.kind === 'image' && !!a.imageId) as Array<
    Extract<AdjuntoDeMensaje, { kind: 'image' }>
  >;
  const subidas = adjuntos.filter((a) => a.kind === 'image' && !a.imageId).length;
  for (const c of creaciones) {
    partes.push(`creación suya (image_id=${c.imageId}; reutilizable con source "generada")`);
    aMano = true;
  }
  if (subidas > 0) {
    // Con el índice del mensaje, cada subida tiene un id con el que el agente la puede
    // pasar como referencia en un turno posterior, aunque ya no la vea
    // (ADR-2026-09-22-las-imagenes-subidas-se-reusan-por-adjunto-id).
    const ids = iMensaje === undefined ? [] : idsDeImagenesSubidas(adjuntos, iMensaje);
    partes.push(
      `${subidas} ${plural(subidas, 'imagen subida', 'imágenes subidas')}${ids.length ? ` (adjunto_id=${ids.join(', ')})` : ''}`,
    );
    if (ids.length) subidasConId = true;
    else sueltos = true;
  }
  for (const a of adjuntos) {
    switch (a.kind) {
      case 'image':
        break;
      case 'product':
        partes.push(`producto "${a.name}" (product_id=${a.productId})`);
        aMano = true;
        break;
      case 'model':
        partes.push(a.modelId ? `modelo "${a.name}" (model_id=${a.modelId})` : `modelo "${a.name}" (de su lista de modelos)`);
        aMano = true;
        break;
      case 'referencia': {
        // Con el id, para que el agente la pueda volver a usar en otro mensaje sin que el
        // usuario la re-adjunte (ADR-2026-09-14-la-pose-y-el-fondo-son-referencias-por-id).
        const idRef = idsDeRefs.get(a);
        const conId = idRef ? ` (adjunto_id=${idRef})` : '';
        partes.push(
          a.tipo === 'pose'
            ? `pose "${a.name}"${a.refId ? ` (pose_id=${a.refId})` : ''}`
            : a.tipo === 'fondo'
              ? `fondo "${a.name}"${a.refId ? ` (fondo_id=${a.refId})` : ''}`
              : a.tipo === 'elemento'
                ? `elemento de marca "${a.name}"${conId}`
                : a.tipo === 'tipografia'
                  ? `tipografía "${a.name}" (su hoja de muestra${idRef ? `, adjunto_id=${idRef}` : ''})`
                  : a.tipo === 'pinterest'
                    ? `imagen de su Pinterest "${a.name}" (inspiración)`
                    : `foto del estilo "${a.name}"${conId}`,
        );
        // Pose y fondo con id se reusan por el suyo; el resto, por su adjunto_id. Lo que no
        // tiene ninguno (guardado antes de que se guardara su path) sigue siendo suelto.
        if (idRef) referenciasConId = true;
        else if (!((a.tipo === 'pose' || a.tipo === 'fondo') && a.refId)) sueltos = true;
        break;
      }
      case 'moodboard':
        // Con el ID: el nombre no identifica (puede haber dos iguales y se puede
        // renombrar la carpeta después de adjuntarla), y `ver_moodboard` resuelve el id
        // antes que el nombre.
        partes.push(
          `estilo "${a.name}" (${fotosDelMoodboard(a)}${a.fotoIds ? ', las que eligió' : ''}; ver_moodboard con moodboard="${a.moodboardId}")`,
        );
        aMano = true;
        break;
      case 'texto':
        partes.push(`archivo de texto "${a.name}" (su contenido viajó sólo en ese turno)`);
        break;
      case 'planilla':
        partes.push(`planilla "${a.name}" (ver_planilla)`);
        aMano = true;
        break;
      case 'pdf':
        partes.push(`PDF "${a.name}" (ver_pdf)`);
        aMano = true;
        break;
      case 'video':
        partes.push(`video "${a.name}" (ver_video)`);
        aMano = true;
        break;
      case 'lote':
        partes.push(`montón de ${a.cantidad} fotos (lote_id=${a.loteId}; ver_fotos)`);
        aMano = true;
        break;
    }
  }
  const notas: string[] = [];
  if (aMano) notas.push('lo de catálogo y lo que se lee con una herramienta sigue a mano: usalo sin pedirle que lo vuelva a elegir');
  if (subidasConId)
    notas.push(
      'las imágenes subidas ya no las ves: mirálas de nuevo con ver_fotos pasando esos ids en `fotos`, y usalas como referencia con source "adjunto" + adjunto_id (y etiqueta con qué es cada una), sin pedírselas de nuevo',
    );
  if (referenciasConId)
    notas.push(
      'las referencias con adjunto_id (estilo, elemento de marca, tipografía) siguen guardadas: usalas con source "adjunto" + adjunto_id, con su etiqueta (la tipografía, "Tipografía de la marca «nombre»", y TAL CUAL: mismo peso, grosor, espaciado y color, sin sombras, luces ni efectos, salvo que el usuario pida otra cosa), sin pedírselas de nuevo',
    );
  if (sueltos) notas.push('las imágenes y referencias sueltas ya no viajan: si la necesitás como referencia, pedile que la vuelva a adjuntar');
  return `\n[Adjuntó en este mensaje: ${partes.join(' · ')}${notas.length ? ` — ${notas.join('; ')}` : ''}]`;
}

// ── Imágenes subidas por id ────────────────────────────────────────────────
// ADR-2026-09-22-las-imagenes-subidas-se-reusan-por-adjunto-id. Una imagen subida no
// viaja en los turnos siguientes (cada foto se paga en todos los turnos), pero sigue en
// Storage: con un id derivado de su POSICIÓN —mensaje y orden dentro del mensaje— el
// agente la puede pasar como `reference_images` sin que el usuario la vuelva a adjuntar.
// La posición es estable porque `messages` se persiste entero y nunca se reordena; no
// hace falta guardar nada nuevo, así que vale para los chats que ya existen.

/** El id de la k-ésima imagen SUBIDA (sin `imageId`) del mensaje `iMensaje` (0-based). */
export const idDeAdjunto = (iMensaje: number, k: number): string => `m${iMensaje + 1}-${k + 1}`;

const RE_ADJUNTO_ID = /^m(\d+)-(\d+)$/;

/**
 * El id de la k-ésima REFERENCIA guardada (foto de estilo, elemento de marca, tipografía: las
 * que tienen su path en Storage) del mensaje `iMensaje` — ADR-2026-10-20-las-referencias-se-reusan-entre-turnos.
 * Otro espacio que el de las subidas (`r`): sumarlas a la cuenta de `m3-2` corría los ids de
 * las imágenes de los chats que ya existen.
 */
export const idDeReferencia = (iMensaje: number, k: number): string => `m${iMensaje + 1}-r${k + 1}`;

const RE_REFERENCIA_ID = /^m(\d+)-r(\d+)$/;

/** Las referencias de un mensaje que se pueden reusar por id: las que guardaron su path. */
const referenciasReusables = (adjuntos: AdjuntoDeMensaje[]) =>
  adjuntos.filter(
    (a): a is Extract<AdjuntoDeMensaje, { kind: 'referencia' }> =>
      a.kind === 'referencia' && !!a.storagePath && (a.tipo === 'moodboard' || a.tipo === 'elemento' || a.tipo === 'tipografia'),
  );

/** El id de cada referencia reusable de un mensaje, en su orden. */
export function idsDeReferencias(adjuntos: AdjuntoDeMensaje[], iMensaje: number): Map<AdjuntoDeMensaje, string> {
  return new Map(referenciasReusables(adjuntos).map((a, k) => [a, idDeReferencia(iMensaje, k)]));
}

/** Los ids de las imágenes subidas de un mensaje, en su orden. */
export function idsDeImagenesSubidas(adjuntos: AdjuntoDeMensaje[], iMensaje: number): string[] {
  const out: string[] = [];
  let k = 0;
  for (const a of adjuntos) {
    if (a.kind !== 'image' || a.imageId) continue;
    out.push(idDeAdjunto(iMensaje, k));
    k++;
  }
  return out;
}

/**
 * Una imagen que el agente trajo de una página web (`abrir_web`,
 * ADR-2026-10-15-el-agente-abre-paginas-web). El backend ya la guardó en la carpeta de adjuntos
 * del chat; queda en el mensaje del AGENTE que la trajo y se usa como cualquier adjunto, por
 * su id (`web-…`): referencia, moodboard, producto, mirarla de nuevo.
 */
export interface ImagenDeLaWeb {
  id: string;
  storagePath: string;
  bucket?: string;
  /** De dónde salió (para contarlo; nunca se manda a un motor). */
  urlOriginal?: string;
  alt?: string;
}

// `pdf-…`: una foto que el agente sacó de un PDF adjunto (`extraer_fotos_del_pdf`,
// ADR-2026-10-18-el-agente-saca-las-fotos-de-un-pdf). Se guarda igual que las de la web.
// `marca-…`: un elemento de marca que el agente miró con `ver_elementos_de_marca`
// (ADR-2026-10-20-el-agente-usa-los-elementos-de-marca), con su path en `brand-assets`.
const RE_IMAGEN_WEB = /^(?:web|pdf|marca)-[0-9a-f]{12}$/;
export const esIdDeImagenWeb = (id: string): boolean => RE_IMAGEN_WEB.test(id);

/** La imagen subida (o traída de la web) que nombra un `adjunto_id`, o null si no existe. El
 *  id de una subida se compara con el que el marcador le dio (mismo cálculo), así que apunta a
 *  la misma foto; el de una web se busca en los mensajes del agente que la trajeron. */
export function adjuntoPorId(
  mensajes: MensajeConAdjuntos[],
  id: unknown,
): Extract<AdjuntoDeMensaje, { kind: 'image' }> | null {
  if (typeof id === 'string' && esIdDeImagenWeb(id.trim())) {
    const buscado = id.trim();
    for (let i = mensajes.length - 1; i >= 0; i--) {
      const web = mensajes[i].imagenesWeb?.find((w) => w.id === buscado);
      if (web) return { kind: 'image', storagePath: web.storagePath, ...(web.bucket ? { bucket: web.bucket } : {}) };
    }
    return null;
  }
  const r = typeof id === 'string' ? RE_REFERENCIA_ID.exec(id.trim()) : null;
  if (r) {
    const mensaje = mensajes[Number(r[1]) - 1];
    if (!mensaje || mensaje.role !== 'user' || !mensaje.adjuntos) return null;
    const ref = referenciasReusables(mensaje.adjuntos)[Number(r[2]) - 1];
    // Se devuelve como una imagen guardada: quien la usa (la referencia de una generación,
    // `ver_fotos`) sólo necesita su path y su bucket.
    return ref?.storagePath ? { kind: 'image', storagePath: ref.storagePath, ...(ref.bucket ? { bucket: ref.bucket } : {}) } : null;
  }
  const m = typeof id === 'string' ? RE_ADJUNTO_ID.exec(id.trim()) : null;
  if (!m) return null;
  const iMensaje = Number(m[1]) - 1;
  const k = Number(m[2]) - 1;
  const mensaje = mensajes[iMensaje];
  if (!mensaje || mensaje.role !== 'user' || !mensaje.adjuntos) return null;
  const subidas = mensaje.adjuntos.filter(
    (a): a is Extract<AdjuntoDeMensaje, { kind: 'image' }> => a.kind === 'image' && !a.imageId,
  );
  return subidas[k] ?? null;
}

/** Lo mínimo de un mensaje que estas cuentas miran. `V2ChatMessage` lo cumple. */
export interface MensajeConAdjuntos {
  role: 'user' | 'assistant';
  adjuntos?: AdjuntoDeMensaje[];
  imagenesWeb?: ImagenDeLaWeb[];
}

/**
 * Las IMÁGENES del último mensaje del usuario que adjuntó alguna. Es lo que
 * `agregar_productos` usa cuando el pedido es "agregá esto como producto" sobre fotos
 * que ya se mandaron — en ese mismo turno o en uno anterior.
 */
export function ultimasImagenesAdjuntadas(
  mensajes: MensajeConAdjuntos[],
): Array<Extract<AdjuntoDeMensaje, { kind: 'image' }>> {
  for (let i = mensajes.length - 1; i >= 0; i--) {
    const m = mensajes[i];
    if (m.role !== 'user' || !m.adjuntos) continue;
    const imagenes = m.adjuntos.filter(
      (a): a is Extract<AdjuntoDeMensaje, { kind: 'image' }> => a.kind === 'image',
    );
    if (imagenes.length > 0) return imagenes;
  }
  return [];
}

/** El último lote adjuntado (por si el agente no pasó `lote_id`). */
export function ultimoLoteAdjuntado(
  mensajes: MensajeConAdjuntos[],
): Extract<AdjuntoDeMensaje, { kind: 'lote' }> | null {
  for (let i = mensajes.length - 1; i >= 0; i--) {
    const m = mensajes[i];
    if (m.role !== 'user' || !m.adjuntos) continue;
    const lote = m.adjuntos.find(
      (a): a is Extract<AdjuntoDeMensaje, { kind: 'lote' }> => a.kind === 'lote',
    );
    if (lote) return lote;
  }
  return null;
}

// ── Documentos: elegir CUÁL, no "el último" ────────────────────────────────
// El input de archivos es `multiple`: el usuario puede mandar tres presupuestos de una
// (ADR-2026-09-09-los-documentos-se-eligen-por-nombre). Quedarse con el primero del
// último mensaje que trae uno dejaba a los otros DOS inalcanzables —el agente los ve
// nombrados en el contexto, pide "el de septiembre" y le llega el de julio, sin manera de
// notarlo— y adjuntar uno nuevo tapaba a todos los anteriores.

/** Todos los adjuntos de ese tipo en la conversación, del MÁS NUEVO al más viejo. */
function documentosAdjuntados<K extends AdjuntoDeMensaje['kind']>(
  mensajes: MensajeConAdjuntos[],
  kind: K,
): Array<Extract<AdjuntoDeMensaje, { kind: K }>> {
  const out: Array<Extract<AdjuntoDeMensaje, { kind: K }>> = [];
  for (let i = mensajes.length - 1; i >= 0; i--) {
    const m = mensajes[i];
    if (m.role !== 'user' || !m.adjuntos) continue;
    for (const a of m.adjuntos) {
      if (a.kind === kind) out.push(a as Extract<AdjuntoDeMensaje, { kind: K }>);
    }
  }
  return out;
}

/** Lo que la tool necesita saber para contestar bien, incluso cuando falla. */
export interface DocumentoElegido<T> {
  /** El que hay que leer, o null si no hay ninguno o el nombre pedido no existe. */
  elegido: T | null;
  /** Los nombres de todos, del más nuevo al más viejo. Con esto la tool le puede decir
   *  al agente cuáles tiene en vez de leerle otro archivo en silencio. */
  nombres: string[];
  /** El agente nombró un archivo que no está (pero hay otros): no es lo mismo que "no
   *  adjuntó nada" y no se contesta igual. */
  noEncontrado: boolean;
}

const normalizar = (s: string): string => s.trim().toLowerCase();
const sinExtension = (s: string): string => normalizar(s).replace(/\.[^.]+$/, '');

/**
 * Cuál de los documentos adjuntos pidió el agente.
 *
 * Sin `archivo`, el más reciente — que es el caso normal y lo que hacía antes. Con
 * `archivo`, el que se llame así: exacto, después ignorando la extensión (el modelo copia
 * "presupuesto" de una frase del usuario) y por último por parecido. Empatados, gana el
 * más nuevo. Si nombró algo que no existe NO se cae al último: eso es justamente el error
 * silencioso que se está arreglando.
 */
function elegirDocumento<T extends { name: string }>(
  documentos: T[],
  archivo?: string | null,
): DocumentoElegido<T> {
  const nombres = documentos.map((d) => d.name);
  const pedido = typeof archivo === 'string' ? archivo.trim() : '';
  if (!pedido) return { elegido: documentos[0] ?? null, nombres, noEncontrado: false };
  const p = normalizar(pedido);
  const ps = sinExtension(pedido);
  const match =
    documentos.find((d) => normalizar(d.name) === p) ??
    documentos.find((d) => sinExtension(d.name) === ps) ??
    documentos.find((d) => normalizar(d.name).includes(p) || p.includes(sinExtension(d.name)));
  return {
    elegido: match ?? null,
    nombres,
    noEncontrado: !match && documentos.length > 0,
  };
}

/** La planilla que `ver_planilla` tiene que leer (ADR-2026-09-08-el-agente-lee-y-escribe-planillas). */
export function elegirPlanillaAdjuntada(
  mensajes: MensajeConAdjuntos[],
  archivo?: string | null,
): DocumentoElegido<Extract<AdjuntoDeMensaje, { kind: 'planilla' }>> {
  return elegirDocumento(documentosAdjuntados(mensajes, 'planilla'), archivo);
}

/** El PDF que `ver_pdf` tiene que leer. */
export function elegirPdfAdjuntado(
  mensajes: MensajeConAdjuntos[],
  archivo?: string | null,
): DocumentoElegido<Extract<AdjuntoDeMensaje, { kind: 'pdf' }>> {
  return elegirDocumento(documentosAdjuntados(mensajes, 'pdf'), archivo);
}

/** El video que `ver_video` tiene que analizar (ADR-2026-09-11-el-agente-entiende-videos-con-gemini). */
export function elegirVideoAdjuntado(
  mensajes: MensajeConAdjuntos[],
  archivo?: string | null,
): DocumentoElegido<Extract<AdjuntoDeMensaje, { kind: 'video' }>> {
  return elegirDocumento(documentosAdjuntados(mensajes, 'video'), archivo);
}

/** La última planilla adjuntada. La usa la card de lanzamientos, que trabaja sobre la que
 *  el usuario acaba de mandar y no tiene a quién preguntarle cuál. */
export function ultimaPlanillaAdjuntada(
  mensajes: MensajeConAdjuntos[],
): Extract<AdjuntoDeMensaje, { kind: 'planilla' }> | null {
  return elegirPlanillaAdjuntada(mensajes).elegido;
}

/**
 * Los ids de registro de los documentos que estos mensajes referencian.
 *
 * Los bytes de un PDF y las filas de una planilla NO viajan en el mensaje: viven en un
 * `Map` a nivel de módulo (`pdfsEnMemoria`, `planillasEnMemoria`) del que el mensaje sólo
 * guarda la llave. Un `Map` de módulo no se desmonta con nada, así que al vaciar un chat
 * hay que devolverle esas llaves — si no, la memoria de la pestaña sólo sube y los
 * documentos de la conversación anterior (o de la cuenta anterior) siguen ahí
 * (ADR-2026-09-09-los-documentos-en-memoria-se-liberan).
 */
export function documentosEnMemoriaDe(mensajes: MensajeConAdjuntos[]): {
  pdfs: string[];
  planillas: string[];
} {
  const pdfs: string[] = [];
  const planillas: string[] = [];
  for (const m of mensajes) {
    for (const a of m.adjuntos ?? []) {
      if (a.kind === 'pdf') pdfs.push(a.pdfId);
      else if (a.kind === 'planilla') planillas.push(a.planillaId);
    }
  }
  return { pdfs, planillas };
}

/**
 * El camino de VUELTA de `resumirAdjunto`: el resumen guardado en un mensaje viejo →
 * un chip enganchable otra vez — ADR-2026-09-14-la-foto-del-mensaje-se-vuelve-a-adjuntar.
 *
 * Lo usa el visor que se abre al tocar una foto en la burbuja del usuario: su único botón
 * es "Agregar al chat", y esto es lo que ese botón necesita construir.
 *
 * **`url` la pone el llamador y es la que la miniatura ya resolvió para dibujarse.** Ésa es
 * la razón de que esta función sea pura y síncrona: el resumen guardado no tiene URL —no
 * puede tenerla, una firma vence en una hora
 * (ADR-2026-09-14-la-referencia-guardada-no-puede-ser-una-firma)— pero para mostrar la
 * miniatura alguien ya la firmó recién, así que no hace falta pedirla de nuevo.
 *
 * Esa URL fresca se puede ENGANCHAR (se usa dentro del turno) pero no se puede GUARDAR: lo
 * que vuelva a persistirse sale de `resumirAdjunto`, que la tira y guarda el path. No
 * copiar este patrón al lado de la persistencia.
 *
 * **El id se DERIVA, no se inventa**, y con las mismas convenciones que arma la biblioteca
 * (`prod-<id>`, `model-<id>`, `pose-<id>`, `gen-<id>`, `subida-<path>`). Dos motivos: es lo
 * que deja al resto del sistema seguir leyendo el id —`imageIdDeCreacion` necesita el
 * `gen-` para mandarla como referencia "generada" y `refIdDeAdjunto` el `pose-` para que el
 * agente la nombre por id en vez de describirla
 * (ADR-2026-09-14-la-pose-y-el-fondo-son-referencias-por-id)— y es lo que hace que tocar dos
 * veces la misma foto no deje dos chips: `addAttachment` descarta por id.
 *
 * Devuelve `null` para lo que no es una foto (un PDF, una planilla, un lote, la carpeta de
 * un moodboard, un video) y para el modelo sin `modelId`: sin id no hay modelo guardado al
 * que volver, y enganchar uno "suelto" mandaría una selección que el resto de la app no
 * sabe resolver. `adjuntoEsFoto` deja fuera exactamente esos casos —el modelo sin id
 * incluido— así que el visor no se abre para ninguno y devolver null es el cinturón y no el
 * camino normal.
 */
export function adjuntoReenganchado(a: AdjuntoDeMensaje, url: string): ChatAttachment | null {
  switch (a.kind) {
    case 'image':
      return {
        kind: 'image',
        id: a.imageId ? `gen-${a.imageId}` : `subida-${a.storagePath}`,
        previewUrl: url,
        storagePath: a.storagePath,
        ...(a.bucket ? { bucket: a.bucket } : {}),
      };
    case 'product':
      return {
        kind: 'product',
        id: `prod-${a.productId}`,
        productId: a.productId,
        name: a.name,
        imageUrl: url,
      };
    case 'model':
      if (!a.modelId) return null;
      return {
        kind: 'model',
        id: `model-${a.modelId}`,
        selection: { mode: 'preset', id: a.modelId, name: a.name, imageUrl: url },
        name: a.name,
        imageUrl: url,
      };
    case 'referencia':
      return {
        kind: 'referencia',
        // Con `refId` el id vuelve a ser exactamente el que armó la biblioteca. Sin él —una
        // foto de moodboard, que no es una entrada de catálogo— la clave estable que hay es
        // dónde vive la foto; alcanza para no duplicar el chip.
        id: a.refId
          ? `${a.tipo}-${a.refId}`
          : `moodboard-${a.storagePath ?? a.imageUrl ?? url}`,
        tipo: a.tipo,
        name: a.name,
        imageUrl: url,
      };
    default:
      return null;
  }
}

/**
 * ¿Este adjunto guardado se dibuja como una FOTO **que se puede volver a enganchar**? Es lo
 * que decide si la miniatura abre el visor: donde no hay foto no hay nada que ampliar, y
 * donde no hay con qué reconstruir el chip no hay nada que agregar.
 *
 * Por eso el modelo SIN `modelId` queda afuera aunque dibuje miniatura
 * (ADR-2026-09-15-la-miniatura-que-no-se-puede-reenganchar-no-abre): mientras su URL
 * guardada siga viva se ve la foto, pero `adjuntoReenganchado` devuelve `null` para él, y
 * abrir un visor cuyo único botón no agrega nada es peor que dejar la miniatura quieta.
 * Esta condición y la de `adjuntoReenganchado` tienen que moverse juntas.
 */
export const adjuntoEsFoto = (a: AdjuntoDeMensaje): boolean =>
  a.kind === 'image' ||
  a.kind === 'product' ||
  (a.kind === 'model' && !!a.modelId) ||
  a.kind === 'referencia';

// ── Una referencia "adjunto" que no existe ────────────────────────────────
/** Lo que una card mira de cada `reference_images[i]` para saber si la adjunta existe. */
export interface SpecDeAdjunto {
  source?: string;
  imagen?: number;
  adjunto_id?: string;
  foto_id?: string;
  /** Una imagen guardada que el usuario sumó desde la card: existe por definición. */
  storage_path?: string;
  etiqueta?: string;
}

const COMO_PEDIRLA =
  'No se generó nada. Pedile que adjunte la foto: en el lienzo, que la seleccione y escriba el pedido (se adjunta sola) o use "Agregar al chat" en el menú de esa imagen; en el chat, con el "+". Después volvé a generar.';

/**
 * El motivo por el que una referencia `source: "adjunto"` NO se puede resolver, o `null`
 * si están todas. Antes, una adjunta que no existía se descartaba en silencio y la
 * generación salía igual, sólo con el texto: el agente creía que había usado la foto y
 * el usuario recibía otra prenda
 * (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir).
 *
 * `adjuntasDelTurno` es cuántas imágenes de tipo `adjunto` viajaron con el último envío
 * (las que numera "Imagen N"). Un `adjunto_id` se busca en los mensajes, como al
 * resolverlo. Una `foto_id` (una del montón) tiene su propio camino y no se mira acá.
 */
export function adjuntaQueFalta(
  specs: readonly SpecDeAdjunto[],
  adjuntasDelTurno: number,
  mensajes: MensajeConAdjuntos[],
): string | null {
  for (const s of specs) {
    if (s.source !== 'adjunto' || s.foto_id || s.storage_path) continue;
    if (s.adjunto_id) {
      if (!adjuntoPorId(mensajes, s.adjunto_id)) {
        return `No hay ninguna imagen subida con adjunto_id=${s.adjunto_id}. ${COMO_PEDIRLA}`;
      }
      continue;
    }
    if (adjuntasDelTurno === 0) {
      return `Pediste usar una imagen adjunta, pero el usuario no adjuntó ninguna imagen en este mensaje. ${COMO_PEDIRLA}`;
    }
    if (Number.isInteger(s.imagen) && (s.imagen as number) > adjuntasDelTurno) {
      return `Pediste la Imagen ${s.imagen} adjunta, pero el usuario adjuntó ${adjuntasDelTurno}. ${COMO_PEDIRLA}`;
    }
  }
  return null;
}

// ── Menciones con `@` en el texto del mensaje ──────────────────────────────
// ADR-2026-10-20-las-menciones-del-chat-se-ven-y-se-abren. Al mandar, `@Nombre` nombra un
// adjunto de ESE mensaje (`nombreDeMencion`: las fotos sueltas como "Imagen N", el resto por
// su nombre). Lo guardado alcanza para volver a encontrarlas y dibujarlas en azul.

/** Con qué nombre se mencionó cada adjunto guardado: el mismo cálculo que `nombreDeMencion`
 *  sobre los adjuntos vivos. Lo que no tiene nombre (el montón de fotos) no se menciona. */
export function nombreDeMencionGuardada(a: AdjuntoDeMensaje, adjuntos: readonly AdjuntoDeMensaje[]): string | null {
  if (a.kind === 'image') {
    const imagenes = adjuntos.filter((x) => x.kind === 'image');
    return `Imagen ${imagenes.indexOf(a) + 1}`;
  }
  if (a.kind === 'lote') return null;
  return 'name' in a && typeof a.name === 'string' && a.name.trim() ? a.name : null;
}

export type ParteDelTexto = { texto: string } | { mencion: string; adjunto: AdjuntoDeMensaje };

const plano = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * El texto partido en tramos y menciones. Una mención es `@` + el nombre de un adjunto del
 * mensaje, sin importar mayúsculas ni acentos, al principio o después de un espacio, salto o
 * paréntesis. Con dos nombres que empiezan igual ("Remera" y "Remera oversize") gana el más
 * largo.
 */
export function partesConMenciones(texto: string, adjuntos: readonly AdjuntoDeMensaje[] | undefined): ParteDelTexto[] {
  if (!texto) return [];
  const nombres = (adjuntos ?? [])
    .map((a) => ({ a, nombre: nombreDeMencionGuardada(a, adjuntos ?? []) }))
    .filter((x): x is { a: AdjuntoDeMensaje; nombre: string } => !!x.nombre)
    .sort((x, y) => y.nombre.length - x.nombre.length);
  if (nombres.length === 0) return [{ texto }];
  const partes: ParteDelTexto[] = [];
  let desde = 0;
  let i = 0;
  while (i < texto.length) {
    const inicioValido = texto[i] === '@' && (i === 0 || /[\s(]/.test(texto[i - 1]));
    const encontrado = inicioValido
      ? nombres.find(({ nombre }) => {
          const tramo = texto.slice(i + 1, i + 1 + nombre.length);
          const despues = texto[i + 1 + nombre.length];
          // Termina en el nombre: "@Imagen 1" no es "@Imagen 12".
          return plano(tramo) === plano(nombre) && (despues === undefined || !/[\p{L}\p{N}]/u.test(despues));
        })
      : undefined;
    if (!encontrado) {
      i++;
      continue;
    }
    if (i > desde) partes.push({ texto: texto.slice(desde, i) });
    const fin = i + 1 + encontrado.nombre.length;
    partes.push({ mencion: texto.slice(i, fin), adjunto: encontrado.a });
    desde = fin;
    i = fin;
  }
  if (desde < texto.length) partes.push({ texto: texto.slice(desde) });
  return partes;
}
